import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowRight, BookOpen, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function BlogListPage({ theme, toggleTheme }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);

    const fetchArticles = async () => {
      try {
        const q = query(
          collection(db, 'articles'),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const articlesData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          let dateStr = 'تاريخ غير معروف';
          if (data.createdAt) {
            const d = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
            dateStr = d.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
          }
          return {
            id: doc.id,
            ...data,
            dateStr,
          };
        });
        setArticles(articlesData);
      } catch (error) {
        console.error("Error fetching articles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 dark:bg-zinc-950 font-sans transition-colors duration-500 overflow-x-hidden" dir="rtl">
      <Navbar theme={theme} toggleTheme={toggleTheme} />

      <main className="flex-grow pt-24 pb-20 relative">
        {/* Background Gradients */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary-600/10 rounded-full blur-[120px] dark:opacity-20 pointer-events-none mix-blend-multiply dark:mix-blend-lighten"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-[120px] dark:opacity-10 pointer-events-none mix-blend-multiply dark:mix-blend-lighten"></div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">

          <div className="text-center max-w-3xl mx-auto mb-16 pt-8 animate-in slide-in-from-bottom-8 duration-700">
            <div className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 text-mdfont-bold mb-6">
              <BookOpen className="w-4 h-4" />
              <span>المدونة</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight text-slate-900 dark:text-white">
              أحدث المقالات والإلهام من <span className="text-primary-600">لباب</span>
            </h1>
            <p className="text-xl text-slate-600 dark:text-zinc-400 font-medium">
              نشاركك هنا خلاصة معرفتنا في مجالات التصميم، البرمجة، التسويق والتطوير
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center p-20 text-slate-500 min-h-[40vh]">
              <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
              <p className="font-bold text-lg">جاري تحميل المقالات...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center p-20 min-h-[40vh] bg-white/50 dark:bg-white/5 backdrop-blur-md rounded-3xl border border-slate-200 dark:border-white/10">
              <p className="text-xl font-bold text-slate-500 dark:text-zinc-400">لا توجد مقالات حالياً، تحقق لاحقاً!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, i) => (
                <div
                  className="group bg-white dark:bg-zinc-900 rounded-lg border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-primary-600/10 dark:hover:shadow-primary-600/5 transition-all duration-500 hover:-translate-y-2 animate-in fade-in slide-in-from-bottom-8"
                  style={{ animationDelay: `${i * 100}ms` }}
                  key={article.id}
                >
                  <Link to={`/blog/${article.id}`} className="block overflow-hidden relative aspect-[16/10]">
                    {article.coverImageUrl ? (
                      <img
                        src={article.coverImageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-slate-300 dark:text-zinc-700" />
                      </div>
                    )}
                    <div className="absolute top-4 right-4 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-md font-bold text-primary-600 dark:text-primary-400 border border-white/20">
                      {article.category || 'عام'}
                    </div>
                  </Link>

                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-4 text-md font-bold text-slate-500 dark:text-zinc-400 mb-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{article.dateStr}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5" />
                        <span>{article.authorName}</span>
                      </div>
                    </div>

                    <Link to={`/blog/${article.id}`} className="block mb-4">
                      <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white line-clamp-2 leading-snug group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {article.title}
                      </h3>
                    </Link>

                    <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-100 dark:border-white/10">
                      <div className="text-mdfont-bold text-slate-500 dark:text-zinc-400">
                        {article.likesCount || 0} إعجاب ❤️
                      </div>
                      <Link to={`/blog/${article.id}`} className="inline-flex items-center gap-2 text-mdfont-bold text-primary-600 dark:text-primary-400 group-hover:gap-3 transition-all">
                        <span>اقرأ المزيد</span>
                        <ArrowRight className="w-4 h-4 rotate-180" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
